// Cliente HTTP para la API administrativa del Servidor MCP de SHC.
// Expuesta por Nginx en /mcp-admin/ (reenviada a 127.0.0.1:8002/admin/...).
// Reutiliza el mismo JWT que el resto de la PWA (Authorization: Bearer ...).

import axios, { AxiosInstance } from 'axios';
import { getServerUrl } from '../config';
import { getToken } from './auth';

const getMcpAdminBaseUrl = (): string => {
  const serverUrl = getServerUrl();
  return `${serverUrl.replace(/\/$/, '')}/mcp-admin`;
};

const mcpAdminApi: AxiosInstance = axios.create({
  baseURL: getMcpAdminBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

mcpAdminApi.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Nota: no hay interceptor de respuesta 401 aquí. Un fallo de firma JWT sólo en
// shc-mcp (p. ej. SHC_MCP_JWT_SECRET_KEY desalineado) no debe cerrar toda la
// sesión PWA: `api.ts` ya expulsa cuando el backend Flask rechaza el token.

// ----------------- Tipos -----------------

export type TokenState = 'active' | 'revoked' | 'expired';

export interface AIUserLite {
  id: number;
  username: string;
  email: string | null;
  nombre: string | null;
  role: string;
  enabled: boolean;
}

export interface AIToken {
  id: number;
  user_id: number;
  username: string;
  email: string | null;
  name: string;
  token_prefix: string;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  note: string | null;
  state: TokenState;
  // Cantidad de invocaciones registradas para este token. Se usa para
  // mostrar un warning antes del borrado físico si es > 0.
  invocations_count?: number | null;
  // Hito 3: capacidad de control. Default false en BD para tokens existentes
  // pre-Hito 3 (no son retroactivos).
  can_control?: boolean;
  for_pwa?: boolean;
  paused?: boolean;
  // Hito 2.2: tools "destildadas" para este token. `[]` significa que el
  // token ve TODAS las tools. La PWA usa este array para precargar los
  // checkboxes en el diálogo de edición.
  excluded_tools?: string[];
}

export interface AITokenCreated extends AIToken {
  token_raw: string;
  role?: string;
}

// Hito 2.2 — Catálogo canónico de tools que el despliegue MCP registra. La
// PWA lo carga al montar el sub-tab de AI Tokens y lo usa para pintar los
// checkboxes en el diálogo de crear/editar.
export interface McpServerInfo {
  server_name: string;
  server_version: string;
  fqdn_public: string | null;
  mcp_url: string | null;
  oauth_enabled: boolean;
}

export type ToolCategory = 'control' | 'viewing' | 'other';

export interface ToolCatalogEntry {
  name: string;
  category: ToolCategory;
  // Posición 1-based en el orden real de registro del servidor.
  order: number;
}

// Hito 3 — Política IA por dispositivo (whitelist/blacklist/confirm/reason).
// La fila completa que devuelve el listado del backend con metadatos del
// dispositivo (nombre, habitación, artefacto, pin_required del propio device).
export interface DevicePolicyRow {
  device_id: number;
  device_nombre: string | null;
  tipo: string | null;
  habitacion_id: number | null;
  habitacion_nombre: string | null;
  artefacto_nombre: string | null;
  pin_required: boolean;
  whitelisted: boolean;
  blacklisted: boolean;
  requires_confirmation: boolean;
  reason_note: string | null;
  updated_at: string | null;
  updated_by_user_id: number | null;
  updated_by_username: string | null;
}

// Política "cruda" tal como la devuelve GET /device-policy/{id} (sin metadata
// del dispositivo). Es null cuando el dispositivo nunca tuvo política guardada.
export interface DevicePolicy {
  device_id: number;
  whitelisted: boolean;
  blacklisted: boolean;
  requires_confirmation: boolean;
  reason_note: string | null;
  updated_by_user_id: number | null;
  updated_at: string | null;
}

export interface AIInvocation {
  id: number;
  timestamp: string | null;
  user_id: number | null;
  username: string | null;
  // Snapshot del email del usuario al momento del evento (sobrevive al
  // DELETE físico del usuario).
  user_email: string | null;
  token_id: number | null;
  // Snapshot del token al momento del evento (sobrevive al DELETE físico
  // del token).
  token_name: string | null;
  token_prefix: string | null;
  session_id: string | null;
  tool_name: string;
  arguments_redacted: any;
  status: string;
  result_summary: string | null;
  error_message: string | null;
  duration_ms: number | null;
  ip_origin: string | null;
  client_user_agent: string | null;
  client_hint: string | null;
  // Hito OAuth: distingue invocaciones autenticadas con token Bearer (Hito 1)
  // de las autenticadas via OAuth 2.1. `oauth_client_id` se popula sólo
  // cuando auth_method='oauth' y queda como snapshot aunque el cliente sea
  // borrado de ai_oauth_clients en el futuro.
  auth_method?: 'bearer_token' | 'oauth';
  oauth_client_id?: string | null;
}

// ----------------- Endpoints -----------------

export async function listUsers(): Promise<AIUserLite[]> {
  const res = await mcpAdminApi.get<{ users: AIUserLite[] }>('/users');
  return res.data.users;
}

export async function listTokens(opts?: {
  includeRevoked?: boolean;
  user?: string;
}): Promise<AIToken[]> {
  const params: Record<string, string> = {};
  if (opts?.includeRevoked) params.include_revoked = 'true';
  if (opts?.user) params.user = opts.user;
  const res = await mcpAdminApi.get<{ tokens: AIToken[] }>('/tokens', { params });
  return res.data.tokens;
}

export async function createToken(input: {
  user_id: number;
  name: string;
  expires_days?: number | null;
  note?: string | null;
  can_control?: boolean;
  for_pwa?: boolean;
  excluded_tools?: string[];
}): Promise<AITokenCreated> {
  const res = await mcpAdminApi.post<AITokenCreated>('/tokens', input);
  return res.data;
}

// Hito 2.2 — Catálogo canónico de tools del despliegue. La respuesta llega
// ya ordenada por `order` (orden de registro del servidor).
export async function getToolsCatalog(): Promise<ToolCatalogEntry[]> {
  const res = await mcpAdminApi.get<{ tools: ToolCatalogEntry[]; total: number }>(
    '/tools-catalog',
  );
  return res.data.tools;
}

// Hito 2.2 — Reemplaza el array excluded_tools de un token. `[]` deja el
// token sin restricciones (ve todas las tools).
export async function updateTokenExcludedTools(
  tokenId: number,
  excludedTools: string[],
): Promise<AIToken | null> {
  const res = await mcpAdminApi.patch<{ ok: boolean; token: AIToken | null }>(
    `/tokens/${tokenId}/excluded-tools`,
    { excluded_tools: excludedTools },
  );
  return res.data.token;
}

// Hito 3 — habilita/deshabilita el flag can_control de un token sin recrearlo.
// Backend: PATCH /admin/tokens/{id}/can-control body: { can_control: bool }
export async function updateTokenCanControl(
  tokenId: number,
  canControl: boolean,
): Promise<AIToken | null> {
  const res = await mcpAdminApi.patch<{ ok: boolean; token: AIToken | null }>(
    `/tokens/${tokenId}/can-control`,
    { can_control: canControl },
  );
  return res.data.token;
}

export async function updateTokenPaused(
  tokenId: number,
  paused: boolean,
): Promise<AIToken | null> {
  const res = await mcpAdminApi.patch<{ ok: boolean; token: AIToken | null }>(
    `/tokens/${tokenId}/paused`,
    { paused },
  );
  return res.data.token;
}

export async function updateTokenForPwa(
  tokenId: number,
  forPwa: boolean,
): Promise<AIToken | null> {
  const res = await mcpAdminApi.patch<{ ok: boolean; token: AIToken | null }>(
    `/tokens/${tokenId}/for-pwa`,
    { for_pwa: forPwa },
  );
  return res.data.token;
}

export async function revokeToken(tokenId: number): Promise<AIToken | null> {
  const res = await mcpAdminApi.post<{ ok: boolean; token: AIToken | null }>(
    `/tokens/${tokenId}/revoke`,
  );
  return res.data.token;
}

// Borra físicamente un token (activo, vencido o revocado). La auditoría en
// ai_tool_invocations conserva snapshot del token.
export async function deleteToken(tokenId: number): Promise<void> {
  await mcpAdminApi.delete(`/tokens/${tokenId}`);
}

// Borra físicamente todos los tokens con revoked_at IS NOT NULL.
// Devuelve la cantidad eliminada para mostrar en el snackbar.
export async function deleteAllRevokedTokens(): Promise<number> {
  const res = await mcpAdminApi.delete<{ ok: boolean; deleted_count: number }>(
    '/tokens/revoked',
  );
  return res.data.deleted_count;
}

// ----------------- Hito 3 — Política IA por dispositivo -----------------

export async function listDevicePolicies(opts?: {
  q?: string;
  roomId?: number;
  onlyWhitelisted?: boolean;
  limit?: number;
  offset?: number;
}): Promise<DevicePolicyRow[]> {
  const params: Record<string, string | number> = {};
  if (opts?.q) params.q = opts.q;
  if (opts?.roomId != null) params.room_id = opts.roomId;
  if (opts?.onlyWhitelisted) params.only_whitelisted = 'true';
  if (opts?.limit != null) params.limit = opts.limit;
  if (opts?.offset != null) params.offset = opts.offset;
  const res = await mcpAdminApi.get<{ policies: DevicePolicyRow[] }>(
    '/device-policy',
    { params },
  );
  return res.data.policies;
}

// Devuelve la política "cruda" de un dispositivo. Si todavía no existe,
// `policy` viene en null y el caller debe asumir defaults all-false.
export async function getDevicePolicy(deviceId: number): Promise<DevicePolicy | null> {
  const res = await mcpAdminApi.get<{ device_id: number; policy: DevicePolicy | null }>(
    `/device-policy/${deviceId}`,
  );
  return res.data.policy;
}

// El backend devuelve la fila "cruda" recién upserteada (sin metadata del
// dispositivo). El caller debe combinar con datos que ya tenga si necesita
// mostrar nombre/habitación/etc.
export async function upsertDevicePolicy(
  deviceId: number,
  input: {
    whitelisted: boolean;
    blacklisted: boolean;
    requires_confirmation: boolean;
    reason_note: string | null;
  },
): Promise<DevicePolicy> {
  const res = await mcpAdminApi.put<{ ok: boolean; device_id: number; policy: DevicePolicy }>(
    `/device-policy/${deviceId}`,
    input,
  );
  return res.data.policy;
}

export async function deleteDevicePolicy(deviceId: number): Promise<boolean> {
  const res = await mcpAdminApi.delete<{ ok: boolean; removed: boolean }>(
    `/device-policy/${deviceId}`,
  );
  return res.data.removed;
}

// ----------------- Política IA por parlante Sonos -----------------

export interface SonosPolicy {
  whitelist: boolean;
  blacklist: boolean;
  require_confirmation: boolean;
  defined?: boolean;
}

export async function getSonosPolicy(speakerId: number): Promise<SonosPolicy | null> {
  const res = await mcpAdminApi.get<{ speaker_id: number; policy: SonosPolicy | null }>(
    `/sonos-device-policy/${speakerId}`,
  );
  const p = res.data.policy;
  if (!p || !p.defined) return null;
  return p;
}

export async function upsertSonosPolicy(
  speakerId: number,
  input: {
    whitelist: boolean;
    blacklist: boolean;
    require_confirmation: boolean;
  },
): Promise<SonosPolicy> {
  const res = await mcpAdminApi.put<{ ok: boolean; speaker_id: number; policy: SonosPolicy }>(
    `/sonos-device-policy/${speakerId}`,
    input,
  );
  return res.data.policy;
}

export async function deleteSonosPolicy(speakerId: number): Promise<boolean> {
  const res = await mcpAdminApi.delete<{ ok: boolean; removed: boolean }>(
    `/sonos-device-policy/${speakerId}`,
  );
  return res.data.removed;
}

// Fila "cruda" de política IA por parlante (solo parlantes CON política definida).
export interface SonosPolicyRow {
  sonos_speaker_id: number;
  whitelist: boolean;
  blacklist: boolean;
  require_confirmation: boolean;
  speaker_nombre?: string | null;
  habitacion_id?: number | null;
}

// Lista todas las políticas IA de parlantes (para el lookup de badges/filtros,
// análogo a listDevicePolicies pero para Sonos). Solo admin.
export async function listSonosPolicies(): Promise<SonosPolicyRow[]> {
  const res = await mcpAdminApi.get<{ policies: SonosPolicyRow[] }>(
    '/sonos-device-policy',
  );
  return res.data.policies || [];
}

// ----------------- Política IA por dispositivo Samsung -----------------

export interface SamsungPolicy {
  whitelist: boolean;
  blacklist: boolean;
  require_confirmation: boolean;
  defined?: boolean;
}

export async function getSamsungPolicy(deviceId: number): Promise<SamsungPolicy | null> {
  const res = await mcpAdminApi.get<{ device_id: number; policy: SamsungPolicy | null }>(
    `/samsung-device-policy/${deviceId}`,
  );
  const p = res.data.policy;
  if (!p || !p.defined) return null;
  return p;
}

export async function upsertSamsungPolicy(
  deviceId: number,
  input: {
    whitelist: boolean;
    blacklist: boolean;
    require_confirmation: boolean;
  },
): Promise<SamsungPolicy> {
  const res = await mcpAdminApi.put<{ ok: boolean; device_id: number; policy: SamsungPolicy }>(
    `/samsung-device-policy/${deviceId}`,
    input,
  );
  return res.data.policy;
}

export async function deleteSamsungPolicy(deviceId: number): Promise<boolean> {
  const res = await mcpAdminApi.delete<{ ok: boolean; removed: boolean }>(
    `/samsung-device-policy/${deviceId}`,
  );
  return res.data.removed;
}

export interface SamsungPolicyRow {
  samsung_device_id: number;
  whitelist: boolean;
  blacklist: boolean;
  require_confirmation: boolean;
  device_nombre?: string | null;
  habitacion_id?: number | null;
}

export async function listSamsungPolicies(): Promise<SamsungPolicyRow[]> {
  const res = await mcpAdminApi.get<{ policies: SamsungPolicyRow[] }>(
    '/samsung-device-policy',
  );
  return res.data.policies || [];
}

export async function getMcpServerInfo(): Promise<McpServerInfo> {
  const res = await mcpAdminApi.get<McpServerInfo>('/server-info');
  return res.data;
}

export async function listInvocations(opts?: {
  tokenId?: number;
  userId?: number;
  limit?: number;
  // Hito OAuth: filtrar por client_id del registro DCR.
  oauthClientId?: string;
}): Promise<AIInvocation[]> {
  const params: Record<string, string | number> = {};
  if (opts?.tokenId != null) params.token_id = opts.tokenId;
  if (opts?.userId != null) params.user_id = opts.userId;
  if (opts?.limit != null) params.limit = opts.limit;
  if (opts?.oauthClientId) params.oauth_client_id = opts.oauthClientId;
  const res = await mcpAdminApi.get<{ invocations: AIInvocation[] }>(
    '/invocations',
    { params },
  );
  return res.data.invocations;
}

export default mcpAdminApi;
